import {
    AmcScanField,
    AmountField,
    BooleanField,
    CheckboxField,
    CreditAuthorizationField,
    DateField,
    EventCell,
    EyeIcon,
    PatientInactiveBadge,
    PatientStatusBadge,
    SelectField,
    TextField,
} from "../components/AdministrationFields";
import { ChronicBillingCell } from "../components/ChronicBillingCell";
import { GuardianContactField } from "../components/GuardianContact";
import { RobotPdaHistoryPanel } from "../components/HistoryModals";
import { AMO_OPTIONS, HORIZONTAL_TABLE_COLUMN_COUNT, SUB_COLUMNS } from "../model/administrationConstants";
import {
    formatDate,
    formatPatientName,
    isAmcDateEnabled,
    isAutomaticDebitAuthorizationEnabled,
    normalizeCreditAuthorization,
} from "../model/administrationRules";
import {
    cellStyle,
    emptyCellStyle,
    groupHeaderStyleFor,
    iconButtonStyle,
    pdaHistoryActionsStyle,
    pdaHistoryButtonStyle,
    pdaRemarkWithHistoryStyle,
    primaryButtonStyle,
    secondaryButtonStyle,
    sectionCellStyle,
    stickyActionsCellStyle,
    stickyActionsHeaderStyle,
    subHeaderStyleFor,
} from "../styles/administrationModalStyles";
import {
    groupHeaderStyle,
    labelStyle,
    patientDetailStyle,
    patientIdentityActionsStyle,
    patientIdentityStyle,
    patientNameWithBadgeStyle,
    stickyPatientCellStyle,
    stickyPatientHeaderStyle,
    tableContainerStyle,
    tableStyle,
} from "../styles/administrationStyles";

// Nom, naissance, chambre, NIR, sous-catégorie et accès à l’identité et aux documents.
function HorizontalIdentityCell({ view, current }) {
    const { setDocumentRow, setIdentityRow } = view;
    return (
            <td style={{ ...cellStyle, ...stickyPatientCellStyle }}>
                <div style={patientIdentityStyle}>
                    <div style={{ minWidth: 0 }}>
                        <span style={patientNameWithBadgeStyle}>
                            <strong style={{ color: "#111827", overflowWrap: "anywhere" }}>
                                {formatPatientName(current)}
                            </strong>
                            <PatientStatusBadge status={current.patientStatus} />
                            <PatientInactiveBadge inactive={current.inactive} />
                        </span>
                        <span style={patientDetailStyle}>Né(e) le {formatDate(current.dateNaissance)}</span>
                        <span style={patientDetailStyle}>Chambre {current.chambre || "—"}</span>
                        <span style={patientDetailStyle}>NIR {current.nir || "—"}</span>
                        {current.productionSubcategory && <span style={patientDetailStyle}>Sous-catégorie {current.productionSubcategory}</span>}
                    </div>
                    <div style={patientIdentityActionsStyle}>
                        <button
                            type="button"
                            aria-label={`Modifier l'identité de ${formatPatientName(current)}`}
                            title="Modifier l'identité"
                            onClick={() => setIdentityRow(current)}
                            style={iconButtonStyle}
                        >
                            ✎
                        </button>
                        <button
                            type="button"
                            aria-label={`Voir les documents de ${formatPatientName(current)}`}
                            title="Voir les documents"
                            onClick={() => setDocumentRow(current)}
                            style={iconButtonStyle}
                        >
                            <EyeIcon />
                        </button>
                    </div>
                </div>
            </td>
    );
}

// Colonnes du groupe Administration.
function HorizontalAdministrationCells({ view, row, current, dirty }) {
    const { updateRowDraft } = view;
    return (
        <>
                <td style={sectionCellStyle("administration", dirty)}>
                    <CreditAuthorizationField
                        row={current}
                        onChange={value => updateRowDraft(row, {
                            creditAuthorization: value,
                            creditAuthorizationType: null,
                            creditAuthorizationAmount: null,
                        })}
                    />
                </td>
                <td style={sectionCellStyle("administration", dirty)}>
                    <AmountField
                        label={`Montant autorisation crédit ${formatPatientName(current)}`}
                        value={current.creditAuthorizationAmount}
                        disabled={normalizeCreditAuthorization(current) !== "Oui"}
                        onChange={value => updateRowDraft(row, { creditAuthorizationAmount: value })}
                    />
                </td>
                <td style={sectionCellStyle("administration", dirty)}><TextField value={current.creditNote} onChange={value => updateRowDraft(row, { creditNote: value })} multiline /></td>
                <td style={sectionCellStyle("administration", dirty)}><SelectField value={current.amoStatus} options={AMO_OPTIONS} onChange={value => updateRowDraft(row, { amoStatus: value })} /></td>
                <td style={sectionCellStyle("administration", dirty)}>
                    <AmcScanField
                        value={current.amcScanReceived}
                        onChange={value => updateRowDraft(row, { amcScanReceived: value, ...(!isAmcDateEnabled(value) ? { amcDate: null } : {}) })}
                    />
                </td>
                <td style={sectionCellStyle("administration", dirty)}>
                    <DateField
                        value={current.amcDate}
                        disabled={!isAmcDateEnabled(current.amcScanReceived)}
                        onChange={value => updateRowDraft(row, { amcDate: value })}
                    />
                </td>
                <td style={sectionCellStyle("administration", dirty)}>
                    <CheckboxField
                        label={`Autorisation de prélèvement automatique ${formatPatientName(current)}`}
                        checked={isAutomaticDebitAuthorizationEnabled(current.scanAuto)}
                        onChange={checked => updateRowDraft(row, { scanAuto: checked ? "Oui" : "Non" })}
                    />
                </td>
                <td style={sectionCellStyle("administration", dirty)}>
                    <GuardianContactField
                        status={current.guardianContactStatus}
                        contact={current.guardianContactValue}
                        onPatch={patch => updateRowDraft(row, patch)}
                    />
                </td>
                <td style={sectionCellStyle("administration", dirty)}><DateField value={current.guardianSentDate} onChange={value => updateRowDraft(row, { guardianSentDate: value })} /></td>
                <td style={sectionCellStyle("administration", dirty)}><TextField value={current.correspondingPharmacistDeclaration} onChange={value => updateRowDraft(row, { correspondingPharmacistDeclaration: value })} multiline /></td>
                <td style={sectionCellStyle("administration", dirty)}><TextField value={current.administrativeRemarks} onChange={value => updateRowDraft(row, { administrativeRemarks: value })} multiline /></td>
        </>
    );
}

// Colonnes facturation admission, BPM et PDA.
function HorizontalCareCells({ view, row, current, dirty, saving }) {
    const { archivePdaComment, pdaHistoryRefreshKey, setEventsModal, setRobotHistoryRow, updateRowDraft } = view;
    return (
        <>
                <td style={sectionCellStyle("admission", dirty)}><DateField value={current.admissionAssessmentDate} onChange={value => updateRowDraft(row, { admissionAssessmentDate: value })} /></td>
                <td style={sectionCellStyle("admission", dirty)}><DateField value={current.admissionInterviewDate} onChange={value => updateRowDraft(row, { admissionInterviewDate: value })} /></td>
                <td style={sectionCellStyle("admission", dirty)}><TextField value={current.admissionInterviewNote} onChange={value => updateRowDraft(row, { admissionInterviewNote: value })} multiline /></td>
                <td style={sectionCellStyle("admission", dirty)}><DateField value={current.admissionMembershipDate} onChange={value => updateRowDraft(row, { admissionMembershipDate: value })} /></td>
                <td style={sectionCellStyle("bpm", dirty)}><TextField value={current.bpmInitialInterview} onChange={value => updateRowDraft(row, { bpmInitialInterview: value })} multiline /></td>
                <td style={sectionCellStyle("bpm", dirty)}><TextField value={current.bpmYear1TreatmentAnalysis} onChange={value => updateRowDraft(row, { bpmYear1TreatmentAnalysis: value })} multiline /></td>
                <td style={sectionCellStyle("bpm", dirty)}><TextField value={current.bpmYear1FollowUpInterview} onChange={value => updateRowDraft(row, { bpmYear1FollowUpInterview: value })} multiline /></td>
                <td style={sectionCellStyle("bpm", dirty)}><TextField value={current.bpmYear1ObservanceInterview} onChange={value => updateRowDraft(row, { bpmYear1ObservanceInterview: value })} multiline /></td>
                <td style={sectionCellStyle("admission", dirty)}><EventCell row={row} type="FLU" vaccination onOpen={(eventRow, type, vaccination) => setEventsModal({ row: eventRow, type, vaccination })} /></td>
                <td style={sectionCellStyle("admission", dirty)}><EventCell row={row} type="COVID" vaccination onOpen={(eventRow, type, vaccination) => setEventsModal({ row: eventRow, type, vaccination })} /></td>
                <td style={sectionCellStyle("pda", dirty)}>
                    <div style={pdaRemarkWithHistoryStyle}>
                        <TextField label="Remarque production" value={current.pdaProductionRemark} onChange={value => updateRowDraft(row, { pdaProductionRemark: value })} multiline />
                        <div style={pdaHistoryActionsStyle}>
                            <button
                                type="button"
                                onClick={() => archivePdaComment(current)}
                                disabled={!String(current.pdaProductionRemark || "").trim() || saving}
                                style={{
                                    ...pdaHistoryButtonStyle,
                                    opacity: !String(current.pdaProductionRemark || "").trim() || saving ? 0.55 : 1,
                                    cursor: !String(current.pdaProductionRemark || "").trim() || saving ? "not-allowed" : "pointer",
                                }}
                            >
                                Ajouter à l'historique
                            </button>
                            <button type="button" onClick={() => setRobotHistoryRow(row)} style={pdaHistoryButtonStyle}>Commentaires PDA</button>
                        </div>
                        <RobotPdaHistoryPanel row={row} refreshKey={pdaHistoryRefreshKey} />
                    </div>
                </td>
                <td style={sectionCellStyle("pda", dirty)}><TextField value={current.antalgique} onChange={value => updateRowDraft(row, { antalgique: value })} multiline /></td>
        </>
    );
}

// Colonnes insuline, Zopiclone, produits chers, chronique et facturation.
function HorizontalBillingCells({ view, row, current, dirty }) {
    const { refreshEventPatient, setBillingHistoryRow, setChronicProductRow, setEventsModal, updateRowDraft, user } = view;
    return (
        <>
                <td style={sectionCellStyle("insulin", dirty)}><BooleanField value={current.insulin} onChange={value => updateRowDraft(row, { insulin: value })} /></td>
                <td style={sectionCellStyle("insulin", dirty)}><TextField value={current.authorizedInvoiceCount} onChange={value => updateRowDraft(row, { authorizedInvoiceCount: value })} /></td>
                <td style={sectionCellStyle("insulin", dirty)}><EventCell row={row} type="INSULIN" onOpen={(eventRow, type, vaccination) => setEventsModal({ row: eventRow, type, vaccination })} /></td>
                <td style={sectionCellStyle("zopiclone", dirty)}><BooleanField value={current.zopiclone} onChange={value => updateRowDraft(row, { zopiclone: value })} /></td>
                <td style={sectionCellStyle("zopiclone", dirty)}><EventCell row={row} type="ZOPICLONE" onOpen={(eventRow, type, vaccination) => setEventsModal({ row: eventRow, type, vaccination })} /></td>
                <td style={sectionCellStyle("expensiveProducts", dirty)}><TextField value={current.expensiveProducts} onChange={value => updateRowDraft(row, { expensiveProducts: value })} multiline /></td>
                <td style={sectionCellStyle("expensiveProducts", dirty)}><EventCell row={row} type="EXPENSIVE_PRODUCT" onOpen={(eventRow, type, vaccination) => setEventsModal({ row: eventRow, type, vaccination })} /></td>
                <td style={sectionCellStyle("chronic", dirty)}>
                    <div style={{ display: "grid", gap: "10px" }}>
                        <label style={{ ...labelStyle, gap: "4px" }}>
                            Dernière facturation
                            <DateField
                                value={current.chronicLastBillingDate}
                                label={`Dernière facturation chronique ${formatPatientName(current)}`}
                                onChange={value => updateRowDraft(row, { chronicLastBillingDate: value })}
                            />
                        </label>
                        <ChronicBillingCell
                            row={current}
                            user={user}
                            onOpenProducts={eventRow => setChronicProductRow(eventRow)}
                            onOpenHistory={eventRow => setBillingHistoryRow(eventRow)}
                            onChanged={refreshEventPatient}
                        />
                    </div>
                </td>
                <td style={sectionCellStyle("billingRemarks", dirty)}><TextField value={current.billingRemarks} onChange={value => updateRowDraft(row, { billingRemarks: value })} multiline /></td>
                <td style={sectionCellStyle("toInvoice", dirty)}><TextField value={current.toInvoice} onChange={value => updateRowDraft(row, { toInvoice: value })} multiline /></td>
        </>
    );
}

// Enregistrer, annuler ou ouvrir l’historique de la ligne.
function HorizontalActionsCell({ view, row, dirty, saving }) {
    const { cancelRow, saveRow, setHistoryRow } = view;
    return (
            <td style={{ ...cellStyle, ...stickyActionsCellStyle }}>
                {dirty ? (
                    <div style={{ display: "grid", gap: "7px" }}>
                        <button type="button" disabled={saving} onClick={() => saveRow(row.patientId)} style={primaryButtonStyle}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
                        <button type="button" disabled={saving} onClick={() => cancelRow(row.patientId)} style={secondaryButtonStyle}>Annuler</button>
                    </div>
                ) : (
                    <div style={{ display: "grid", gap: "7px" }}>
                        <button type="button" onClick={() => setHistoryRow(row)} style={secondaryButtonStyle}>Historique</button>
                        {row.updatedAt && <small style={{ color: "#9ca3af" }}>{new Date(row.updatedAt).toLocaleDateString("fr-FR")}<br />{row.updatedBy}</small>}
                    </div>
                )}
            </td>
    );
}

export function renderHorizontalTable(view) {
    const { drafts, filteredRows, handlePatientsScroll, hasMorePatients, loading, loadingMorePatients, savingIds } = view;
    return (
    <div style={tableContainerStyle} onScroll={handlePatientsScroll}>
        <table style={tableStyle}>
        <thead>
            <tr>
                <th rowSpan={2} style={{ ...groupHeaderStyle, ...stickyPatientHeaderStyle }}>Résident</th>
                <th colSpan={11} style={groupHeaderStyleFor("administration")}>Administration</th>
                <th colSpan={6} style={groupHeaderStyleFor("admission")}>Facturation admission</th>
                <th colSpan={4} style={groupHeaderStyleFor("bpm")}>BPM</th>
                <th colSpan={2} style={groupHeaderStyleFor("pda")}>PDA</th>
                <th colSpan={3} style={groupHeaderStyleFor("insulin")}>Insuline</th>
                <th colSpan={2} style={groupHeaderStyleFor("zopiclone")}>Zopiclone</th>
                <th colSpan={2} style={groupHeaderStyleFor("expensiveProducts")}>Produits chers</th>
                <th colSpan={1} style={groupHeaderStyleFor("chronic")}>Facturation chronique</th>
                <th rowSpan={2} style={groupHeaderStyleFor("billingRemarks")}>Remarque facturation / MTE</th>
                <th rowSpan={2} style={groupHeaderStyleFor("toInvoice")}>À facturer</th>
                <th rowSpan={2} style={{ ...groupHeaderStyle, ...stickyActionsHeaderStyle }}>Actions</th>
            </tr>
            <tr>
                {SUB_COLUMNS.map(([title, section], index) => (
                    <th key={`${title}-${index}`} style={subHeaderStyleFor(section)}>{title}</th>
                ))}
            </tr>
        </thead>
        <tbody>
            {loading && <tr><td colSpan={HORIZONTAL_TABLE_COLUMN_COUNT} style={emptyCellStyle}>Chargement…</td></tr>}
            {!loading && filteredRows.length === 0 && <tr><td colSpan={HORIZONTAL_TABLE_COLUMN_COUNT} style={emptyCellStyle}>Aucun résident trouvé.</td></tr>}
            {!loading && filteredRows.map(row => {
                const current = drafts[row.patientId] || row;
                const dirty = Boolean(drafts[row.patientId]);
                const saving = Boolean(savingIds[row.patientId]);
                return (
                    <tr
                        key={row.patientId}
                        className={[
                            current.notInvoiced ? "administration-patient-row--not-invoiced" : "",
                            current.inactive ? "administration-patient-row--inactive" : "",
                        ].filter(Boolean).join(" ")}
                        style={{ background: dirty ? "#eef2ff" : "white" }}
                    >
                        <HorizontalIdentityCell view={view} current={current} />
                        <HorizontalAdministrationCells view={view} row={row} current={current} dirty={dirty} />
                        <HorizontalCareCells view={view} row={row} current={current} dirty={dirty} saving={saving} />
                        <HorizontalBillingCells view={view} row={row} current={current} dirty={dirty} />
                        <HorizontalActionsCell view={view} row={row} dirty={dirty} saving={saving} />
                    </tr>
                );
            })}
            {!loading && loadingMorePatients && <tr><td colSpan={HORIZONTAL_TABLE_COLUMN_COUNT} style={emptyCellStyle}>Chargement des résidents suivants…</td></tr>}
            {!loading && !loadingMorePatients && hasMorePatients && <tr><td colSpan={HORIZONTAL_TABLE_COLUMN_COUNT} style={emptyCellStyle}>Faites défiler pour charger la suite.</td></tr>}
        </tbody>
        </table>
    </div>
    );
}
